import React from "react";
import { C, Disc } from "./Disc";
import { ExplodedView, type ExplodedPart } from "./ExplodedView";
const cx = 220;
const label = (y: number, h: number, ry: number) => y + h / 2 + ry / 2;
const statorTeeth = Array.from({
  length: 12
}, (_, i) => i / 12 * Math.PI * 2);
const statorCoils = Array.from({
  length: 11
}, (_, i) => cx - 60 + i * 12);
const magnetSegments = Array.from({
  length: 9
}, (_, i) => cx - 72 + i * 18);
const bellSlots = [-62, -34, -6, 22, 50];
const bellSpokes = [0.35, 2.45, 4.55];
const parts: ExplodedPart[] = [{
  id: "base",
  label: "Mounting plate",
  delta: 180,
  anchorX: cx + 110,
  labelY: label(420, 14, 32),
  node: <>
        <Disc cx={cx} y={420} rx={110} ry={32} h={14} side={C.ink} top={C.inkSoft} topDecor={<g strokeWidth={1.5} data-id="element-376" data-mp="11969n20w2nh2">
              {[[cx - 64, 420], [cx + 64, 420], [cx, 399], [cx, 441]].map(([x, y]) => <ellipse key={`${x}-${y}`} cx={x} cy={y} rx={7} ry={3} fill={C.sunDeep} data-id="element-377" data-mp="3q94do5gcuk6w" />)}
              <ellipse cx={cx} cy={420} rx={14} ry={5} fill={C.ink} stroke={C.cream} data-id="element-378" data-mp="3v87bb4u09o4u" />
            </g>} data-id="element-375" data-mp="nc1bdo7f2swd" />
      </>
}, {
  id: "stator",
  label: "Copper stator",
  delta: 115,
  anchorX: cx + 70,
  labelY: label(365, 40, 20),
  node: <Disc cx={cx} y={365} rx={70} ry={20} h={40} side={C.sunDeep} top={C.inkSoft} sideDecor={<g strokeWidth={1.5} data-id="element-380" data-mp="huc9rziou39o">
            {statorCoils.map(x => <line key={x} x1={x} y1={366} x2={x} y2={404} data-id="element-381" data-mp="msxae01uqoyn" />)}
          </g>} topDecor={<g stroke={C.sunDeep} strokeWidth={4} strokeLinecap="round" data-id="element-382" data-mp="rs9ysp8dg43c">
            {statorTeeth.map(a => <line key={a} x1={cx + Math.cos(a) * 26} y1={365 + Math.sin(a) * 7.5} x2={cx + Math.cos(a) * 60} y2={365 + Math.sin(a) * 17} data-id="element-383" data-mp="wrd1w44sk08o" />)}
            <ellipse cx={cx} cy={365} rx={18} ry={5.5} fill={C.ink} stroke={C.ink} strokeWidth={2} data-id="element-384" data-mp="gs90ssdjut6r" />
          </g>} data-id="element-379" data-mp="8ywkzwwsieay" />
}, {
  id: "magnets",
  label: "Magnet ring",
  delta: 40,
  anchorX: cx + 86,
  labelY: label(360, 16, 25),
  node: <Disc cx={cx} y={360} rx={86} ry={25} h={16} side={C.tomato} top={C.cream} sideDecor={<g strokeWidth={1.5} data-id="element-386" data-mp="zrdam3cx0qzh">
            {magnetSegments.map(x => <line key={x} x1={x} y1={361} x2={x} y2={376} data-id="element-387" data-mp="usa2ub5jw6nt" />)}
          </g>} topDecor={<ellipse cx={cx} cy={361} rx={70} ry={20} fill={C.inkSoft} data-id="element-388" data-mp="31hyf94acccyx" />} data-id="element-385" data-mp="pr2vgd7bl9ho" />
}, {
  id: "bell",
  label: "Rotor bell",
  delta: -76,
  anchorX: cx + 90,
  labelY: label(346, 70, 26),
  node: <Disc cx={cx} y={346} rx={90} ry={26} h={70} side={C.cream} top={C.white} sideDecor={<g fill={C.ink} data-id="element-390" data-mp="1h28pdcb2vujd">
            {bellSlots.map(dx => <rect key={dx} x={cx + dx} y={378} width={12} height={24} rx={3} data-id="element-391" data-mp="12b041fe7kvc1" />)}
          </g>} topDecor={<g data-id="element-392" data-mp="tauwn590lwxv">
            {bellSpokes.map(a => <line key={a} x1={cx} y1={346} x2={cx + Math.cos(a) * 76} y2={346 + Math.sin(a) * 22} strokeWidth={7} strokeLinecap="round" data-id="element-393" data-mp="ta1vqkwqm1nn" />)}
            <ellipse cx={cx} cy={346} rx={18} ry={6} fill={C.ink} data-id="element-394" data-mp="oayvrevm6lmj" />
          </g>} data-id="element-389" data-mp="1c12vvc9lg38s" />
}, {
  id: "prop",
  label: "Propeller",
  delta: -156,
  anchorX: cx + 176,
  labelY: 334,
  node: <g stroke={C.ink} strokeWidth={2.5} strokeLinejoin="round" data-id="element-395" data-mp="2ip9h3dd02xnk">
        <ellipse cx={cx - 88} cy={344} rx={86} ry={14} fill={C.tomato} transform={`rotate(-6 ${cx} 341)`} data-id="element-396" data-mp="io1riksz0tz0" />
        <ellipse cx={cx + 88} cy={338} rx={86} ry={14} fill={C.tomato} transform={`rotate(-6 ${cx} 341)`} data-id="element-397" data-mp="do710id3y6eh" />
        <Disc cx={cx} y={336} rx={20} ry={7} h={10} side={C.tomatoDark} top={C.tomato} data-id="element-398" data-mp="9lsn8coapwu2" />
      </g>
}, {
  id: "nut",
  label: "Prop nut",
  delta: -212,
  anchorX: cx + 14,
  labelY: label(322, 14, 5),
  node: <Disc cx={cx} y={322} rx={14} ry={5} h={14} side={C.sunDeep} top={C.sun} data-id="element-399" data-mp="3vvd2d4utl7s2" />
}];
export function DroneMotor({
  active
}: {
  active: boolean;
}) {
  return <ExplodedView title="Exploded view of a brushless drone motor" parts={parts} active={active} width={700} height={520} horizontal={{
    pivotX: 350,
    pivotY: 390,
    cx,
    topY: 44,
    bottomY: 476
  }}
  // 80% of the available band, centred.
  className="mx-auto h-[80%] min-h-[176px] w-[80%]" data-id="element-400" data-mp="3m3h4nuwnbjod" />;
}